import { useEffect, useRef, useState } from 'react';
import { loadModules } from 'esri-loader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { MapPin, Plus, X, Trash2, CheckCheck, ArrowLeft, Loader2, Eye, AlertTriangle, Mountain, Landmark, Camera } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useRunWaypoints, type RunWaypoint } from '@/hooks/useRunWaypoints';
import AIWaypointBadge from './AIWaypointBadge';
import { Alert, AlertDescription } from './ui/alert';

interface Waypoint extends Partial<RunWaypoint> {
  id: string;
  name: string;
  description?: string;
  geom?: any;
  waypoint_type?: string;
  auto_generated?: boolean;
  photo_urls?: string[];
  detection_type?: string;
  recorded_at?: string;
}

interface RunWaypointEditorProps {
  runId: string;
  trackGeometry?: any;
  photos?: string[];
  onComplete: (waypoints: Partial<RunWaypoint>[]) => void;
  onBack: () => void;
}

const RunWaypointEditor = ({ 
  runId, 
  trackGeometry,
  photos = [],
  onComplete,
  onBack
}: RunWaypointEditorProps) => {
  const mapDiv = useRef<HTMLDivElement>(null);
  const { waypoints: dbWaypoints, isLoading, saveWaypoints } = useRunWaypoints(runId);
  const [waypoints, setWaypoints] = useState<Waypoint[]>([]);
  const [deletedIds, setDeletedIds] = useState<string[]>([]);
  const [selectedWaypoint, setSelectedWaypoint] = useState<Waypoint | null>(null);
  const [isAddingWaypoint, setIsAddingWaypoint] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [view, setView] = useState<any>(null);
  const [isMapLoading, setIsMapLoading] = useState(true);
  const { toast } = useToast();

  // Map detection type to waypoint type
  const mapDetectionTypeToWaypointType = (detectionType?: string): string => {
    const mapping: { [key: string]: string } = {
      'stop': 'rest_stop',
      'turnaround': 'turnaround',
      'elevation_change': 'climb',
      'photo_cluster': 'scenic',
      'manual': 'poi',
      'hiking': 'trailhead'
    };
    return detectionType ? (mapping[detectionType] || 'poi') : 'poi';
  };

  // Initialize waypoints from database
  useEffect(() => {
    if (dbWaypoints.length > 0) {
      const mappedWaypoints = dbWaypoints.map(wp => ({
        ...wp,
        waypoint_type: mapDetectionTypeToWaypointType(wp.detection_type),
      }));
      setWaypoints(mappedWaypoints);
    }
  }, [dbWaypoints]);

  // Initialize map when data is ready and container exists
  useEffect(() => {
    if (isLoading) {
      return;
    }
    if (view) return;
    if (!mapDiv.current) {
      console.log('RunWaypointEditor: waiting for map container to mount');
      return;
    }

    console.log('RunWaypointEditor: Initializing map', { trackGeometry, waypointCount: waypoints.length });

    let mapView: any = null;

    const initializeMap = async () => {
      try {
        console.log('RunWaypointEditor: Loading ArcGIS modules...');
        const [
          Map,
          MapView,
          Graphic,
          SimpleLineSymbol,
          SimpleMarkerSymbol,
          Polyline,
          Point
        ] = await loadModules([
          'esri/Map',
          'esri/views/MapView',
          'esri/Graphic',
          'esri/symbols/SimpleLineSymbol',
          'esri/symbols/SimpleMarkerSymbol',
          'esri/geometry/Polyline',
          'esri/geometry/Point'
        ]);
        
        console.log('RunWaypointEditor: ArcGIS modules loaded successfully');

        const map = new Map({
          basemap: 'topo-vector'
        });

        mapView = new MapView({
          container: mapDiv.current,
          map: map,
          center: [-105.5, 39.7],
          zoom: 10
        });
        
        console.log('RunWaypointEditor: MapView created, waiting for load...');

        await mapView.when();
        console.log('RunWaypointEditor: MapView loaded and ready');

        // Add run track if available
        if (trackGeometry?.coordinates) {
          console.log('RunWaypointEditor: Adding track geometry', trackGeometry.coordinates.length, 'points');
          const lineSymbol = new SimpleLineSymbol({
            color: [232, 93, 58, 0.8],
            width: 4
          });

          const polyline = new Polyline({
            paths: [trackGeometry.coordinates],
            spatialReference: { wkid: 4326 }
          });

          const trackGraphic = new Graphic({
            geometry: polyline,
            symbol: lineSymbol
          });

          mapView.graphics.add(trackGraphic);
          await mapView.goTo(trackGraphic.geometry.extent.expand(1.3));
          console.log('RunWaypointEditor: Zoomed to track extent');
        } else {
          console.log('RunWaypointEditor: No track geometry available');
        }

        // Add existing waypoints
        waypoints.forEach(waypoint => {
          if (waypoint.geom?.coordinates) {
            const [lng, lat] = waypoint.geom.coordinates;
            const symbol = getWaypointSymbol(waypoint.waypoint_type || 'poi', waypoint.auto_generated || false, SimpleMarkerSymbol);
            const point = new Point({
              longitude: lng,
              latitude: lat,
              spatialReference: { wkid: 4326 }
            });

            const graphic = new Graphic({
              geometry: point,
              symbol: symbol,
              attributes: { waypointId: waypoint.id }
            });

            mapView.graphics.add(graphic);
          }
        });

        setView(mapView);
        console.log('RunWaypointEditor: Map initialized successfully');
        setIsMapLoading(false);
      } catch (error) {
        console.error('RunWaypointEditor: Error initializing map:', error);
        setIsMapLoading(false);
        toast({
          title: "Map error",
          description: "Failed to load the map. Please refresh and try again.",
          variant: "destructive",
        });
      }
    };

    initializeMap();

    return () => {
      if (mapView) {
        mapView.destroy();
      }
    };
  }, [isLoading]); // Initialize after data is ready

  // Handle click events separately
  useEffect(() => {
    if (!view) return;

    const clickHandler = view.on('click', (event: any) => {
      if (isAddingWaypoint) {
        loadModules([
          'esri/Graphic',
          'esri/symbols/SimpleMarkerSymbol',
        ]).then(([Graphic, SimpleMarkerSymbol]) => {
          const newWaypoint: Waypoint = {
            id: `temp-${Date.now()}`,
            name: 'New Waypoint',
            geom: {
              type: 'Point',
              coordinates: [event.mapPoint.longitude, event.mapPoint.latitude]
            },
            waypoint_type: 'poi',
            auto_generated: false,
            photo_urls: [],
            recorded_at: new Date().toISOString()
          };
          
          const updatedWaypoints = [...waypoints, newWaypoint];
          setWaypoints(updatedWaypoints);
          setSelectedWaypoint(newWaypoint);
          setIsAddingWaypoint(false);

          // Add graphic to map
          const symbol = getWaypointSymbol('poi', false, SimpleMarkerSymbol);
          const graphic = new Graphic({
            geometry: event.mapPoint,
            symbol: symbol,
            attributes: { waypointId: newWaypoint.id }
          });
          view.graphics.add(graphic);

          toast({
            title: "Waypoint added",
            description: "Click on it to edit details",
          });
        });
      }
    });

    return () => {
      clickHandler.remove();
    };
  }, [view, isAddingWaypoint, waypoints]);

  // Update map when waypoints change
  useEffect(() => {
    if (!view) return;

    // Clear all waypoint graphics and re-add them
    view.graphics.removeAll();

    // Re-add track
    if (trackGeometry?.coordinates) {
      loadModules([
        'esri/Graphic',
        'esri/symbols/SimpleLineSymbol',
        'esri/geometry/Polyline'
      ]).then(([Graphic, SimpleLineSymbol, Polyline]) => {
        const lineSymbol = new SimpleLineSymbol({
          color: [232, 93, 58, 0.5],
          width: 3
        });

        const polyline = new Polyline({
          paths: [trackGeometry.coordinates],
          spatialReference: { wkid: 4326 }
        });

        const trackGraphic = new Graphic({
          geometry: polyline,
          symbol: lineSymbol
        });

        view.graphics.add(trackGraphic);
      });
    }

    // Re-add waypoints
    waypoints.forEach(waypoint => {
      if (waypoint.geom?.coordinates) {
        loadModules([
          'esri/Graphic',
          'esri/symbols/SimpleMarkerSymbol',
          'esri/geometry/Point'
        ]).then(([Graphic, SimpleMarkerSymbol, Point]) => {
          const [lng, lat] = waypoint.geom.coordinates;
          const symbol = getWaypointSymbol(waypoint.waypoint_type || 'poi', waypoint.auto_generated || false, SimpleMarkerSymbol);
          const point = new Point({
            longitude: lng,
            latitude: lat,
            spatialReference: { wkid: 4326 }
          });

          const graphic = new Graphic({
            geometry: point,
            symbol: symbol,
            attributes: { waypointId: waypoint.id }
          });

          view.graphics.add(graphic);
        });
      }
    });
  }, [waypoints, view]);

  const getWaypointSymbol = (type: string, isAI: boolean, SimpleMarkerSymbol: any) => {
    const colors: { [key: string]: number[] } = {
      poi: [100, 149, 237],
      hazard: [220, 53, 69],
      obstacle: [220, 53, 69],
      rest_stop: [40, 167, 69],
      scenic: [255, 193, 7],
      technical: [233, 94, 58],
      camping: [111, 66, 193],
      water: [13, 110, 253],
      fuel: [255, 87, 34],
      landmark: [156, 39, 176]
    };

    return new SimpleMarkerSymbol({
      color: colors[type] || [100, 149, 237],
      size: isAI ? 14 : 12,
      outline: {
        color: isAI ? [255, 215, 0] : [255, 255, 255],
        width: isAI ? 3 : 2
      }
    });
  };

  const handleUpdateWaypoint = (updates: Partial<Waypoint>) => {
    if (!selectedWaypoint) return;

    const updatedWaypoints = waypoints.map(wp =>
      wp.id === selectedWaypoint.id ? { ...wp, ...updates } : wp
    );
    
    setWaypoints(updatedWaypoints);
    setSelectedWaypoint({ ...selectedWaypoint, ...updates });
  };

  const handleDeleteWaypoint = () => {
    if (!selectedWaypoint) return;

    // Track deleted ID if it's a real waypoint (not temp)
    if (selectedWaypoint.id && !selectedWaypoint.id.startsWith('temp-')) {
      setDeletedIds(prev => [...prev, selectedWaypoint.id]);
    }

    const updatedWaypoints = waypoints.filter(wp => wp.id !== selectedWaypoint.id);
    setWaypoints(updatedWaypoints);
    setSelectedWaypoint(null);

    toast({
      title: "Waypoint deleted",
      description: "Waypoint has been removed.",
    });
  };

  const handleAcceptAll = () => {
    toast({
      title: "All waypoints accepted",
      description: `Keeping all ${waypoints.length} waypoints`,
    });
  };

  const handleClearAll = () => {
    const aiWaypoints = waypoints.filter(wp => wp.auto_generated);
    if (aiWaypoints.length === 0) return;

    const manualWaypoints = waypoints.filter(wp => !wp.auto_generated);
    setWaypoints(manualWaypoints);
    setSelectedWaypoint(null);

    toast({
      title: "AI waypoints cleared",
      description: `Removed ${aiWaypoints.length} AI-detected waypoint(s)`,
    });
  };

  const handleSaveAndContinue = async () => {
    setIsSaving(true);
    try {
      const success = await saveWaypoints(waypoints, deletedIds);
      if (success) {
        onComplete(waypoints);
      } else {
        toast({
          title: "Save failed",
          description: "Failed to save waypoints. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error saving waypoints:', error);
      toast({
        title: "Error",
        description: "An error occurred while saving waypoints.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const aiWaypointCount = waypoints.filter(wp => wp.auto_generated).length;
  const manualWaypointCount = waypoints.filter(wp => !wp.auto_generated).length;

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading waypoints...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Review & Edit Waypoints
          </CardTitle>
          <CardDescription>
            {aiWaypointCount > 0 && (
              <span className="block mb-2">
                ✨ {aiWaypointCount} AI-detected waypoint{aiWaypointCount !== 1 ? 's' : ''} from your photos
              </span>
            )}
            Review AI-detected waypoints, add your own, or skip this step
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Map */}
          <div className="relative">
            <div ref={mapDiv} className="w-full h-96 rounded-lg border bg-muted" />
            {isMapLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-background/80 rounded-lg">
                <div className="text-center">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2 text-primary" />
                  <p className="text-sm text-muted-foreground">Loading map...</p>
                </div>
              </div>
            )}
          </div>

          {/* Stats and Controls */}
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="gap-1">
                  <Camera className="h-3 w-3" />
                  {aiWaypointCount} AI
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="gap-1">
                  <MapPin className="h-3 w-3" />
                  {manualWaypointCount} Manual
                </Badge>
              </div>
            </div>

            <div className="flex gap-2">
              {aiWaypointCount > 0 && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAcceptAll}
                  >
                    <CheckCheck className="h-4 w-4 mr-2" />
                    Accept All
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleClearAll}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Clear AI
                  </Button>
                </>
              )}
              <Button
                variant={isAddingWaypoint ? "default" : "outline"}
                size="sm"
                onClick={() => setIsAddingWaypoint(!isAddingWaypoint)}
              >
                <Plus className="h-4 w-4 mr-2" />
                {isAddingWaypoint ? 'Click map to place' : 'Add Manual'}
              </Button>
            </div>
          </div>

          {isAddingWaypoint && (
            <Alert>
              <MapPin className="h-4 w-4" />
              <AlertDescription>
                Click anywhere on the map to add a waypoint at that location
              </AlertDescription>
            </Alert>
          )}

          {/* Waypoint List */}
          {waypoints.length > 0 && (
            <div className="border rounded-lg p-4 space-y-2 max-h-60 overflow-y-auto">
              <h4 className="text-sm font-semibold mb-3">Waypoints ({waypoints.length})</h4>
              {waypoints.map((wp) => (
                <button
                  key={wp.id}
                  onClick={() => setSelectedWaypoint(wp)}
                  className={`w-full text-left p-3 rounded-lg border transition-colors ${
                    selectedWaypoint?.id === wp.id
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{wp.name}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {wp.description || 'No description'}
                      </p>
                    </div>
                    {wp.auto_generated && (
                      <AIWaypointBadge 
                        detectionType={wp.detection_type || 'poi'} 
                        autoGenerated={true} 
                      />
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Waypoint editor */}
          {selectedWaypoint && (
            <div className="p-4 border rounded-lg space-y-4 bg-muted/30">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold flex items-center gap-2">
                  Edit Waypoint
                  {selectedWaypoint.auto_generated && (
                    <AIWaypointBadge 
                      detectionType={selectedWaypoint.detection_type || 'poi'} 
                      autoGenerated={true} 
                    />
                  )}
                </h4>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSelectedWaypoint(null)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="space-y-3">
                <div>
                  <Label htmlFor="waypoint-name">Name</Label>
                  <Input
                    id="waypoint-name"
                    value={selectedWaypoint.name || ''}
                    onChange={(e) => handleUpdateWaypoint({ name: e.target.value })}
                  />
                </div>

                <div>
                  <Label htmlFor="waypoint-type">Type</Label>
                  <Select
                    value={selectedWaypoint.waypoint_type || 'poi'}
                    onValueChange={(value) => handleUpdateWaypoint({ waypoint_type: value })}
                  >
                    <SelectTrigger id="waypoint-type">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent className="bg-background z-50">
                      <SelectItem value="poi">Point of Interest</SelectItem>
                      <SelectItem value="rest_stop">Rest Stop / Break</SelectItem>
                      <SelectItem value="turnaround">Turnaround Point</SelectItem>
                      <SelectItem value="climb">Climb / Descent</SelectItem>
                      <SelectItem value="scenic">Scenic Viewpoint</SelectItem>
                      <SelectItem value="trailhead">Trailhead</SelectItem>
                      <SelectItem value="obstacle">Obstacle</SelectItem>
                      <SelectItem value="hazard">Hazard</SelectItem>
                      <SelectItem value="water_crossing">Water Crossing</SelectItem>
                      <SelectItem value="camp">Campsite</SelectItem>
                      <SelectItem value="landmark">Landmark</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="waypoint-description">Description</Label>
                  <Textarea
                    id="waypoint-description"
                    value={selectedWaypoint.description || ''}
                    onChange={(e) => handleUpdateWaypoint({ description: e.target.value })}
                    placeholder="Add notes about this waypoint..."
                    rows={3}
                  />
                </div>

                {selectedWaypoint.photo_urls && selectedWaypoint.photo_urls.length > 0 && (
                  <div>
                    <Label>Associated Photo</Label>
                    <img 
                      src={selectedWaypoint.photo_urls[0]} 
                      alt="Waypoint" 
                      className="w-full h-32 object-cover rounded-lg mt-2 photo-signature"
                    />
                  </div>
                )}

                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleDeleteWaypoint}
                  className="w-full"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Waypoint
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex gap-4">
        <Button 
          variant="outline"
          onClick={onBack}
          disabled={isSaving}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <Button 
          onClick={handleSaveAndContinue}
          disabled={isSaving}
        >
          {isSaving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            'Save Waypoints'
          )}
        </Button>
      </div>
    </div>
  );
};

export default RunWaypointEditor;
